import * as React from "react";
import { useState } from "react";
import styled from "@mui/material/styles/styled";
import { QuestionsIF } from "../QuestionsIF";
import { DateQuestionPicker } from "./DateQuestionPicker";
import { buildFreeTextQualifier } from "fe-common/logic/qualifiedFeature/freeText";
import { DATE_OF_BIRTH_FEATURE_ID, Relations } from "shared";
import { MeasurementUnits, QualifiedFeature } from "fe-common/graphql";
import { buildAgeFeature } from "fe-common/patientCase/AgeAndGenderQFeatures";
import { DocQuestion } from "../DocQuestion";
import { useTranslate } from "../../../hooks/useTranslate";
import { PatientColorMap } from "../../../layout/patientColorMap";
//region [[ Styles ]]
const DateQuestionView = styled("div")({
  width: "100%",
});
const ErrorText = styled("div")({
  fontSize: "12px",
  fontWeight: 700,
  color: PatientColorMap.Primary,
  margin: "5px 0",
});
//endregion [[ Styles ]]

//region [[ Props ]]
export interface DateQuestionProps extends QuestionsIF {}
//endregion [[ Props ]]

//region [[ Functions ]]
function dateToYYYmmdd(date: Date) {
  if (!date) return "";
  const year = date.getFullYear().toString();
  let month = (date.getMonth() + 1).toString().padStart(2, "0");
  let day = date
    .getDate()
    .toString()
    .padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateToYearsFromNow(date) {
  return Math.floor((Date.now() - date) / (24 * 3600 * 365.25 * 1000));
}
//endregion [[ Functions ]]
export const DateQuestion = ({ ...props }: DateQuestionProps) => {
  const [date, setDate] = useState(null);
  const [errorText, setErrorText] = useState<string>(null);
  const questionQf = props.question?.suggestions?.[0]?.suggestion;
  const { translate } = useTranslate();

  const isValidDate = (d) => {
    const isDate = d instanceof Date && !!d.getTime();
    if (!isDate) return false;
    if (questionQf.feature.id === DATE_OF_BIRTH_FEATURE_ID) {
      const age = dateToYearsFromNow(d);
      if (age < 16) {
        setErrorText(translate("MIN_AGE_ERROR"));
        return false;
      }
      if (age > 120) {
        setErrorText(translate("MAX_AGE_ERROR"));
        return false;
      }
    }
    return true;
  };

  const handleDateChanged = (date) => {
    setDate(date);
    if (!isValidDate(date)) return;
    // Clear previous errors
    setErrorText(null);
    const answers = [
      {
        ...questionQf,
        qualifiers: [
          buildFreeTextQualifier(dateToYYYmmdd(date), Relations.FREE_TEXT),
        ],
      } as QualifiedFeature,
    ];

    //for date of birth calculate age as well
    if (questionQf.feature.id === DATE_OF_BIRTH_FEATURE_ID) {
      const age = dateToYearsFromNow(date);
      answers.push(
        buildAgeFeature({
          low: age,
          high: age,
          units: MeasurementUnits.Years,
          complexUnits: null,
        }),
      );
    }

    props.onAnswer(answers);
  };

  return (
    <DateQuestionView className={"date-question-view"}>
      <DocQuestion
        text={props.question.question?.text}
        miniView={props.miniView}
      />
      <DateQuestionPicker onChange={handleDateChanged} value={date} />
      {!!errorText && <ErrorText>{errorText}</ErrorText>}
    </DateQuestionView>
  );
};
